import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiException } from '@/lib/api/types';
import { forgetAutoLogin, rememberAutoLogin } from '@/lib/autoLogin';
import { changePassword, fetchMe, login, logout, updateProfile } from './api';
import {
  AUTH_ERROR_CODES,
  type ChangePasswordRequest,
  type LoginRequest,
  type Me,
  type UpdateProfileRequest,
} from './types';

/**
 * 인증 상태 훅.
 *
 * Zustand 를 쓰지 않는다. 로그인 사용자 정보는 서버가 가진 상태이지
 * 클라이언트 상태가 아니다. (CLAUDE.md 5.4절)
 * 스토어에 복사해두면 관리자가 권한을 바꿨을 때 화면이 옛 값을 계속 보여주고,
 * "언제 다시 읽을 것인가" 를 직접 관리해야 한다.
 */

export const AUTH_QUERY_KEY = ['auth', 'me'] as const;

/**
 * 현재 로그인 사용자.
 *
 * 로그인하지 않았으면 401(A004)이 떨어지고 data 는 undefined 가 된다.
 * 그 상태를 오류가 아니라 "비로그인" 으로 다룬다.
 */
export function useMe() {
  const query = useQuery<Me, ApiException>({
    queryKey: AUTH_QUERY_KEY,
    queryFn: fetchMe,

    // 🔴 401 은 재시도하지 않는다
    //   기본 재시도 정책은 4xx 를 걸러내지만(main.tsx), 여기서 한 번 더 못박는다.
    //   비로그인 상태에서 앱을 열 때마다 세 번씩 호출하면 로그인 화면이 그만큼 늦게 뜬다.
    retry: false,

    // 창을 다시 볼 때 세션이 아직 살아 있는지 확인한다.
    // 다른 화면의 갱신 정책과 다른 이유는, 세션은 시간이 지나면 끊기는 값이기 때문이다.
    refetchOnWindowFocus: true,

    staleTime: 60_000,
  });

  const isUnauthenticated =
    query.isError && query.error?.code === AUTH_ERROR_CODES.UNAUTHENTICATED;

  return {
    me: query.data,
    /** 아직 세션 확인 중. 이 동안 화면을 그리면 로그인 페이지가 잠깐 깜빡인다 */
    isLoading: query.isLoading,
    isUnauthenticated,
    /** 401 이 아닌 진짜 오류(네트워크, 서버 장애) */
    error: query.isError && !isUnauthenticated ? query.error : undefined,
    refetch: query.refetch,
  };
}

/**
 * 로그인.
 *
 * 성공하면 응답으로 받은 사용자 정보를 그대로 캐시에 넣는다.
 * 다시 /me 를 부르지 않아도 되고, 로그인 직후의 빈 화면 깜빡임이 사라진다.
 */
export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation<Me, ApiException, LoginRequest>({
    mutationFn: login,
    onSuccess: (me, request) => {
      queryClient.setQueryData(AUTH_QUERY_KEY, me);

      /*
        다음에 앱을 열 때 "로그인 중입니다" 를 보여줄지 정하는 표시다.
        자격증명이 아니다 — 아무 권한도 주지 않는다. (lib/autoLogin.ts)
      */
      rememberAutoLogin(request.rememberMe);
    },
  });
}

/**
 * 로그아웃.
 *
 * 서버 세션을 지운 뒤 캐시를 통째로 비운다.
 * 인증 캐시만 지우면 다른 사람이 이어서 로그인했을 때
 * 앞사람의 추천·가계부 데이터가 잠깐 보인다.
 */
export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiException, void>({
    mutationFn: logout,
    onSettled: () => {
      // 서버 호출이 실패해도 클라이언트 상태는 비운다.
      // 네트워크가 끊긴 상태에서 로그아웃 버튼이 아무 반응도 없으면
      // 사용자는 로그아웃되지 않은 줄 안다.
      queryClient.clear();

      /*
        🔴 직접 로그아웃했으면 자동 로그인 표시도 지운다.
          안 지우면 다음 방문에 "로그인 중입니다" 가 떴다가 로그인 화면으로 떨어진다.
          나가겠다는 의사를 화면이 무시하는 셈이다.
      */
      forgetAutoLogin();
    },
  });
}

/**
 * 본인 정보 수정. (내 정보 화면, 3.6)
 *
 * 응답으로 받은 Me 를 캐시에 그대로 넣는다.
 * 헤더에 표시 이름이 떠 있어서, 다시 /me 를 부르게 하면 그 사이에 옛 이름이
 * 남아 "저장이 됐나" 를 의심하게 된다. useLogin 과 같은 이유다.
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation<Me, ApiException, UpdateProfileRequest>({
    mutationFn: updateProfile,
    onSuccess: (me) => {
      queryClient.setQueryData(AUTH_QUERY_KEY, me);
    },
  });
}

/**
 * 본인 비밀번호 변경.
 *
 * 🔴 캐시를 건드리지 않는다
 *   비밀번호는 Me 에 없다. 지금 이 기기의 세션도 그대로 살아 있다.
 *   화면에서 바뀌는 것이 없으므로 무효화할 것도 없다.
 *
 *   다른 기기의 세션만 끊기는데, 그것은 그 기기에서 401 로 드러난다.
 *   여기서 할 수 있는 일이 아니다.
 */
export function useChangePassword() {
  return useMutation<number, ApiException, ChangePasswordRequest>({
    mutationFn: changePassword,
  });
}
